/* ============================================================================
 * Kimes Ranch — Cart dataLayer events
 * ----------------------------------------------------------------------------
 * Pushes `view_cart` and `remove_from_cart` into window.dataLayer so that GTM
 * (container GTM-5BWSVBL) can pick them up via the Custom Event triggers.
 *
 * INSTALL:
 *   1. Upload this file to: Online Store → Themes → Edit code → assets/
 *      and name it kr-cart-events.js
 *   2. Open layout/theme.liquid and add this line just before </body>:
 *        {{ 'kr-cart-events.js' | asset_url | script_tag }}
 *      — OR — paste the IIFE inline inside a <script> tag in the same spot.
 *   3. Nothing else. Verify with `window.dataLayer` in the console.
 *
 * NOTES:
 *   - Prices are divided by 100 because Shopify's Ajax Cart API returns cents.
 *   - view_cart fires on /cart page load AND when the cart drawer is opened.
 *     Drawer selectors are common Shopify patterns; adjust if your theme uses
 *     different classes/attrs.
 *   - remove_from_cart uses fetch interception (works with any theme that uses
 *     Shopify's standard Ajax cart API — /cart/change or /cart/update).
 * ========================================================================== */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  // ---- helpers -------------------------------------------------------------

  var currency = function () {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  };

  var toItems = function (lineItems) {
    return (lineItems || []).map(function (li, idx) {
      return {
        item_id: String(li.variant_id || li.product_id || li.id || ''),
        item_name: li.product_title || li.title || '',
        item_variant: li.variant_title || undefined,
        item_brand: li.vendor || undefined,
        price: ((li.final_price != null ? li.final_price : li.price) || 0) / 100,
        quantity: li.quantity || 1,
        index: idx
      };
    });
  };

  var pushEcom = function (eventName, payload) {
    window.dataLayer.push({ ecommerce: null }); // clear previous ecommerce obj
    window.dataLayer.push({ event: eventName, ecommerce: payload });
  };

  var getCart = function () {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  };

  // ---- view_cart -----------------------------------------------------------

  var fireViewCart = function () {
    getCart()
      .then(function (cart) {
        pushEcom('view_cart', {
          currency: cart.currency || currency(),
          value: (cart.total_price || 0) / 100,
          items: toItems(cart.items)
        });
      })
      .catch(function () {});
  };

  // Fire on /cart page
  if (/^\/cart(\/|\?|$)/.test(location.pathname + location.search)) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fireViewCart, { once: true });
    } else {
      fireViewCart();
    }
  }

  // Fire on cart drawer open (broad selector — covers Dawn, Impulse, Prestige,
  // and most common custom themes). Debounced via a 250 ms settle delay so the
  // drawer has time to render.
  var lastDrawerFire = 0;
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest(
      'a[href="/cart"], a[href^="/cart?"], ' +
      '[data-cart-drawer-toggle], [data-cart-toggle], ' +
      '.js-drawer-open-cart, .header__icon--cart, .cart-link, .cart-icon-bubble'
    );
    if (!trigger) return;
    var now = Date.now();
    if (now - lastDrawerFire < 1000) return; // prevent double fires
    lastDrawerFire = now;
    setTimeout(fireViewCart, 250);
  }, { capture: true, passive: true });

  // ---- remove_from_cart ----------------------------------------------------
  // Intercept Shopify's Ajax Cart mutations. When quantity is set to 0 on a
  // specific line/id, capture the item from the pre-mutation cart snapshot and
  // fire remove_from_cart with the correct item, price, and quantity.

  var origFetch = window.fetch.bind(window);

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartMutation = /\/cart\/(change|update)(\.js)?/.test(url);
    if (!isCartMutation) return origFetch(input, init);

    var requestedQty = null;
    var requestedId = null;
    try {
      var body = init && init.body;
      if (body) {
        var parsed;
        if (typeof body === 'string' && body.trim().charAt(0) === '{') {
          parsed = JSON.parse(body);
        } else if (typeof body === 'string') {
          parsed = {};
          new URLSearchParams(body).forEach(function (v, k) { parsed[k] = v; });
        } else if (body instanceof FormData) {
          parsed = {};
          body.forEach(function (v, k) { parsed[k] = v; });
        }
        if (parsed) {
          requestedQty = Number(parsed.quantity);
          requestedId = parsed.id || parsed.line;
        }
      }
    } catch (_) { /* fall through */ }

    // Only care about removals (qty explicitly 0)
    if (requestedQty !== 0 || !requestedId) return origFetch(input, init);

    // Capture the item before it's removed
    return getCart()
      .catch(function () { return null; })
      .then(function (before) {
        return origFetch(input, init).then(function (res) {
          if (!before || !before.items) return res;
          var removed = before.items.find(function (it, i) {
            return String(it.variant_id) === String(requestedId)
              || String(it.key) === String(requestedId)
              || String(it.id) === String(requestedId)
              || String(i + 1) === String(requestedId); // line-index mode
          });
          if (removed) {
            var items = toItems([removed]);
            var value = items.reduce(function (s, it) { return s + it.price * it.quantity; }, 0);
            pushEcom('remove_from_cart', {
              currency: before.currency || currency(),
              value: value,
              items: items
            });
          }
          return res;
        });
      });
  };
})();
